/**
 * Emissions Activity Detail Page
 *
 * Full-page view of an activity. Composes the discrete sub-views in
 * `components/activity-detail/*` directly: a header band, a top KPI
 * strip, a two-column body (Summary + Notices + Evidence + History on
 * the left, Emission Factor + Methodology + References on the right),
 * and a confirmation modal for the revert flow.
 */

import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ArrowLeft, Gauge, Hash, Loader2, MapPin, Pencil, ShoppingCart } from 'lucide-react';
import { Spinner } from 'reactstrap';
import { Dashboard } from '@g17eco/ui-modern/components/ui/dashboard';
import { Card, CardContent, CardHeader, CardTitle } from '@g17eco/ui-modern/components/ui/card';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { MetricCard } from '@g17eco/ui-modern/components/ui/metric-card';
import { ConfirmationModal } from '@g17eco/molecules/confirm-modal';
import { generateUrl } from '@routes/util';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { FUEL_TYPE_LABELS, GAS_TYPE_LABELS, PROCESS_TYPE_LABELS, VEHICLE_TYPE_LABELS } from '@g17eco/emissions-config';
import {
  useGetActivityQuery,
  useGetCalculationHistoryQuery,
  useRevertCalculationVersionMutation,
} from '../api/emissions';
import { useEmissionsFormatter } from '../hooks/useEmissionsFormatter';
import { emissionsTrackerRoutes } from '../routes';
import { SCOPE_COLORS, SCOPE_LUCIDE_ICONS, SCOPE_TAILWIND } from '../config/scopeBranding';
import { ACTIVITY_TYPE_BRANDING, ACTIVITY_TYPE_LABELS } from '../config/activityTypeBranding';
import { type ActivityRecord, type EmissionCalculation, Scope2Method } from '../types/emissions';
import {
  ActivityBiogenicCard,
  ActivityGasBreakdownCard,
  ActivityInputTable,
  ActivitySupplementaryCard,
  hasGasBreakdownData,
  hasSupplementaryData,
} from '../components/activity-detail/ActivitySummaryView';
import {
  ActivityFactorView,
  ActivityMethodologyView,
  ActivityReferencesView,
} from '../components/activity-detail/ActivityCalculationView';
import { ActivityHistoryView } from '../components/activity-detail/ActivityHistoryView';
import { ActivityNoticesPanel } from '../components/activity-detail/ActivityNoticesPanel';
import { EvidenceTab } from '../components/EvidenceTab';
import { EditActivityModal } from '../components/EditActivityModal';
import { UnitToggle } from '../components/UnitToggle';
import { formatCO2e, SCOPE_LABELS, STATUS_CONFIG } from '../components/activity-detail/formatters';

export const EmissionsActivityDetailPage = () => {
  const { initiativeId, activityId } = useParams<{ initiativeId: string; activityId: string }>();
  const history = useHistory();

  const { settings } = useEmissionsFormatter({ initiativeId });
  const defaultToTonnes = settings.displayUnit !== 'kgCO2e';
  const [showInTonnes, setShowInTonnes] = useState(defaultToTonnes);

  useEffect(() => {
    setShowInTonnes(settings.displayUnit !== 'kgCO2e');
  }, [settings.displayUnit]);

  const {
    data: activity,
    isLoading: isLoadingActivity,
    isError: isActivityError,
  } = useGetActivityQuery({ initiativeId, activityId }, { skip: !initiativeId || !activityId });

  const {
    currentData: calculationHistory,
    isLoading: isLoadingCalc,
    isFetching: isFetchingCalc,
    isError: isCalcError,
  } = useGetCalculationHistoryQuery({ initiativeId, activityId }, { skip: !initiativeId || !activityId });
  const currentCalculation = calculationHistory?.[0];

  const [revertCalculation, { isLoading: isReverting }] = useRevertCalculationVersionMutation();
  const [revertVersion, setRevertVersion] = useState<number | null>(null);

  const handleRevert = async () => {
    if (revertVersion === null) return;
    await revertCalculation({ initiativeId, activityId, targetVersion: revertVersion }).unwrap();
    setRevertVersion(null);
  };

  const navigateBack = () => {
    history.push(generateUrl(emissionsTrackerRoutes.EMISSIONS_ACTIVITIES, { initiativeId }));
  };

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  if (isLoadingActivity || isLoadingCalc) {
    return (
      <Dashboard>
        <div className='tw:text-center tw:py-16 tw:text-muted-foreground'>
          <Loader2 className='tw:size-8 tw:animate-spin tw:mx-auto tw:mb-3' />
          Loading activity...
        </div>
      </Dashboard>
    );
  }

  if (isActivityError || !activity) {
    return (
      <Dashboard>
        <div className='tw:text-center tw:py-16 tw:text-muted-foreground'>
          <p>Activity not found.</p>
          <Button variant='link' onClick={navigateBack} className='tw:mt-4'>
            <ArrowLeft className='tw:size-4 tw:mr-2' />
            Back to Activities
          </Button>
        </div>
      </Dashboard>
    );
  }

  const scopeStyle = SCOPE_TAILWIND[activity.scope];
  const ScopeIcon = SCOPE_LUCIDE_ICONS[activity.scope];
  const statusConfig = currentCalculation ? STATUS_CONFIG[currentCalculation.status] : null;

  const formatProps = { showInTonnes, decimalPlaces: settings.decimalPlaces };

  return (
    <Dashboard>
      <div className='tw:space-y-6'>
        {/* Header */}
        <div>
          <Button variant='link' size='sm' onClick={navigateBack} className='tw:mb-2 tw:-ml-2'>
            <ArrowLeft className='tw:size-4 tw:mr-1' />
            Back to Activities
          </Button>
          <div className='tw:flex tw:items-center tw:justify-between tw:gap-3'>
            <div className='tw:flex tw:items-center tw:gap-3'>
              {ScopeIcon && (
                <div
                  className={cn(
                    'tw:rounded-full tw:flex tw:items-center tw:justify-center tw:size-10',
                    scopeStyle?.iconBg,
                  )}
                >
                  <ScopeIcon className={cn('tw:size-5', scopeStyle?.icon)} />
                </div>
              )}
              <div>
                <h4 className='tw:text-xl tw:font-semibold tw:mb-0 tw:capitalize'>
                  {activity.activityType.replace(/_/g, ' ')}
                </h4>
                <div className='tw:flex tw:items-center tw:gap-2 tw:text-sm tw:text-muted-foreground tw:flex-wrap'>
                  <Badge variant='outline' className={scopeStyle?.icon}>
                    {SCOPE_LABELS[activity.scope]}
                  </Badge>
                  {statusConfig && <Badge className={statusConfig.className}>{statusConfig.label}</Badge>}
                  <span>{activity.region}</span>
                </div>
              </div>
            </div>
            <div className='tw:flex tw:items-center tw:gap-3'>
              <UnitToggle showInTonnes={showInTonnes} onChange={setShowInTonnes} />
              <Button variant='outline' size='sm' onClick={() => setIsEditModalOpen(true)}>
                <Pencil className='tw:size-3.5 tw:mr-1' />
                Edit
              </Button>
            </div>
          </div>
        </div>

        {isCalcError && (
          <Card>
            <CardContent className='tw:py-12 tw:text-center tw:text-muted-foreground'>
              <p>Failed to load calculation details. Please try again.</p>
            </CardContent>
          </Card>
        )}

        {!isCalcError && !currentCalculation && (
          <Card>
            <CardContent className='tw:py-12 tw:text-center tw:text-muted-foreground'>
              <p>This activity has not been calculated yet.</p>
            </CardContent>
          </Card>
        )}

        {currentCalculation && (
          <>
            <KpiStrip calculation={currentCalculation} activity={activity} {...formatProps} />

            <div className='tw:grid tw:grid-cols-1 tw:lg:grid-cols-3 tw:gap-4'>
              <div className='tw:lg:col-span-2 tw:flex tw:flex-col tw:gap-6'>
                <Card>
                  <CardContent className='tw:pt-4'>
                    <ActivityInputTable activityData={activity} />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Evidence</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <EvidenceTab initiativeId={initiativeId} activityId={activityId} />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Notices</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {currentCalculation.notices && currentCalculation.notices.length > 0 ? (
                      <ActivityNoticesPanel notices={currentCalculation.notices} />
                    ) : (
                      <p className='tw:text-sm tw:text-muted-foreground tw:mb-0'>No notices for this calculation.</p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Calculation History</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {isFetchingCalc ? (
                      <div className='tw:py-6 tw:text-center'>
                        <Spinner color='success' size='sm' />
                      </div>
                    ) : (
                      <ActivityHistoryView
                        calculations={calculationHistory ?? []}
                        isLoading={false}
                        showInTonnes={showInTonnes}
                        decimalPlaces={settings.decimalPlaces}
                        onRevert={(version) => setRevertVersion(version)}
                        isReverting={isReverting}
                      />
                    )}
                  </CardContent>
                </Card>
              </div>

              <div className='tw:flex tw:flex-col tw:gap-4'>
                <FactorMetricCard calculation={currentCalculation} />
                <Card>
                  <CardHeader>
                    <CardTitle>Emission Factor</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ActivityFactorView
                      calculation={currentCalculation}
                      activityData={activity}
                      initiativeId={initiativeId}
                      activityId={activityId}
                    />
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle>Methodology</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ActivityMethodologyView calculation={currentCalculation} activityData={activity} />
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle>References &amp; Standards</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ActivityReferencesView calculation={currentCalculation} />
                  </CardContent>
                </Card>
              </div>
            </div>
          </>
        )}

        <ConfirmationModal
          title='Revert Calculation'
          isOpen={revertVersion !== null}
          handleCancel={() => setRevertVersion(null)}
          content={
            <p className='mb-0'>
              This will create a new version with the data from version {revertVersion}. The current calculation will be
              preserved in history.
            </p>
          }
          confirmButton={
            <Button onClick={handleRevert} disabled={isReverting}>
              {isReverting ? 'Reverting...' : 'Revert'}
            </Button>
          }
        />

        {activity && (
          <EditActivityModal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            initiativeId={initiativeId}
            activity={activity}
          />
        )}
      </div>
    </Dashboard>
  );
};

interface KpiStripProps {
  calculation: EmissionCalculation;
  activity: ActivityRecord;
  showInTonnes: boolean;
  decimalPlaces: number;
}

/**
 * Subtext for the activity-quantity KPI card. The card's *label* carries
 * the activity-type name (e.g. "Stationary Combustion"), so the subtext
 * adds specificity rather than echoing the title.
 *
 * Resolution order:
 *   1. The activity's discriminator (fuel/refrigerant/process/vehicle)
 *      — Scope 1 activities always have one.
 *   2. The current factor's name (e.g. "Electricity supplied from grid -
 *      residual mix") — Scope 2 has no fuel field, so the factor name
 *      is the closest substitute.
 */
const quantitySubtext = (
  activity: ActivityRecord,
  calculation: EmissionCalculation,
): string | undefined => {
  if (activity.fuelType) return FUEL_TYPE_LABELS[activity.fuelType];
  if (activity.refrigerantType) return GAS_TYPE_LABELS[activity.refrigerantType];
  if (activity.processType) return PROCESS_TYPE_LABELS[activity.processType];
  if (activity.vehicleType) return VEHICLE_TYPE_LABELS[activity.vehicleType];
  if (calculation.emissionFactorName) return calculation.emissionFactorName;
  return undefined;
};

const KpiStrip = ({ calculation, activity, showInTonnes, decimalPlaces }: KpiStripProps) => {
  const scopeStyle = SCOPE_TAILWIND[calculation.scope];
  const ScopeIcon = SCOPE_LUCIDE_ICONS[calculation.scope];
  const scopeLabel = SCOPE_LABELS[calculation.scope];

  // Dual-reporting (Scope 2): render BOTH location-based and market-based
  // tiles in the strip so the page surfaces the same data the drawer does.
  // The active method gets a "Selected method" subtext.
  const isDual = calculation.locationBasedCo2e !== undefined || calculation.marketBasedCo2e !== undefined;

  const quantityValue = `${activity.quantity.toLocaleString()} ${activity.unit}`;
  const activityBrand = ACTIVITY_TYPE_BRANDING[activity.activityType];
  const ActivityIcon = activityBrand?.Icon;
  const activityLabel = ACTIVITY_TYPE_LABELS[activity.activityType] ?? activity.activityType.replace(/_/g, ' ');

  const formatProps = { showInTonnes, decimalPlaces };

  return (
    <div className='tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:gap-4 tw:lg:grid-cols-4'>
      {isDual ? (
        <>
          {calculation.locationBasedCo2e !== undefined && (
            <MetricCard
              label='Location-Based'
              value={formatCO2e(calculation.locationBasedCo2e, showInTonnes, decimalPlaces)}
              icon={<MapPin className='tw:size-5 tw:text-blue-600' />}
              iconBg='tw:bg-blue-500/10'
              borderColor='blue-500'
              subtext={calculation.scope2Method === Scope2Method.LocationBased ? 'Selected method' : undefined}
            />
          )}
          {calculation.marketBasedCo2e !== undefined && (
            <MetricCard
              label='Market-Based'
              value={formatCO2e(calculation.marketBasedCo2e, showInTonnes, decimalPlaces)}
              icon={<ShoppingCart className='tw:size-5 tw:text-emerald-600' />}
              iconBg='tw:bg-emerald-500/10'
              borderColor='emerald-500'
              subtext={calculation.scope2Method === Scope2Method.MarketBased ? 'Selected method' : undefined}
            />
          )}
        </>
      ) : (
        <MetricCard
          label={`${scopeLabel} Emissions`}
          value={formatCO2e(calculation.co2e, showInTonnes, decimalPlaces)}
          icon={ScopeIcon ? <ScopeIcon className={cn('tw:size-5', scopeStyle?.icon)} /> : undefined}
          iconBg={scopeStyle?.iconBg}
          borderColor={scopeStyle?.border}
          valueColor={SCOPE_COLORS[calculation.scope]}
        />
      )}
      <MetricCard
        label={activityLabel}
        value={quantityValue}
        icon={
          ActivityIcon
            ? <ActivityIcon className={cn('tw:size-5', activityBrand?.iconColor)} />
            : <Hash className='tw:size-5 tw:text-slate-600' />
        }
        iconBg={activityBrand?.iconBg ?? 'tw:bg-slate-500/10'}
        borderColor={activityBrand?.borderColor}
        subtext={quantitySubtext(activity, calculation)}
      />
      {hasGasBreakdownData(calculation) && (
        <ActivityGasBreakdownCard calculation={calculation} {...formatProps} />
      )}
      {hasSupplementaryData(calculation) && (
        <ActivitySupplementaryCard calculation={calculation} {...formatProps} />
      )}
      <ActivityBiogenicCard calculation={calculation} {...formatProps} />
    </div>
  );
};

interface FactorMetricCardProps {
  calculation: EmissionCalculation;
}

const FactorMetricCard = ({ calculation }: FactorMetricCardProps) => {
  const factorValue = `${calculation.emissionFactorValue.toFixed(4)} ${calculation.emissionFactorUnit}`;
  const factorSubtext = [
    calculation.emissionFactorSource,
    calculation.emissionFactorRegion,
    calculation.emissionFactorYear,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <MetricCard
      label='Emission Factor'
      value={factorValue}
      icon={<Gauge className='tw:size-5 tw:text-violet-600' />}
      iconBg='tw:bg-violet-500/10'
      borderColor='violet-500'
      subtext={factorSubtext || undefined}
      // Override MetricCard's default `tw:h-full`: this card sits in
      // the right column's flex stack and would otherwise stretch to
      // match the (much taller) Summary card in the left column.
      className='tw:h-fit'
    />
  );
};

